/**
 * Store Pinia des verrats (composition API)
 */
import { defineStore } from "pinia";
import { ref, computed, DeepReadonly } from "vue";
import BoarService from "../services/BoarService";
import { IBoar } from "../models/boar";
import { IFilterAnimal } from "../models/filterAnimal";
import { StateOfReform } from "../models/reform";
import { IBoarFilter } from "../models/filters/boarFilter";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { IBoarForm } from "../models/forms/boarForm";
import { ICenter } from "../models/center";
import { getDateDiff } from "../helper/dateFormatter";
import { IParameter } from "../models/parameter";
import { useParameterStore } from "./parameter.store";
import { useCenterStore } from "./center.store";
import { useThirdPartiesStore } from "./thirdParties.store";
import { IBoarApiResponse } from "../models/api/boarApiResponse";
import { usePSLotStore } from "./sanitaire/psLot.store";
import { useCTG } from "./ctg.store";
import { useBreedingStud } from "./breedingStuds.store";
import { IKaryotype } from "../models/karyotype";
import { IReform } from "../models/reform";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";
import i18n from "@/i18n";
import { usePerformanceTracker } from "../composables/tools/PerformanceComposable";
import { inFlight } from "../helper/inFlight";

/**
 * Ordre des écritures d'une ligne de verrat, pour `refreshBoars` : la DERNIÈRE relecture
 * DEMANDÉE gagne, pas la dernière réponse arrivée. Chaque écrivain prend un numéro à son départ
 * (relecture, chargement complet) ou à son arrivée (MajBoar, état poussé par le serveur) ; une
 * relecture dont la réponse arrive après une écriture plus récente de la même ligne est ignorée.
 */
let boarWriteSequence = 0;
const lastBoarWrite = new Map<number, number>();
let lastLoadSequence = 0;
const nextBoarWriteSequence = () => ++boarWriteSequence;

/**
 * Store Pinia des verrats utilisant la Composition API
 */
const useBoars = defineStore("boars", () => {
    // State
    const datas = ref<DeepReadonly<IBoar>[]>([]);
    const current = ref<DeepReadonly<IBoar>>({} as IBoar);
    const itemForm = ref<IBoarForm>({} as IBoarForm);
    const isLoading = ref<boolean>(false);
    const loadedCenterId = ref<number | undefined>(undefined);
    const reformedLoaded = ref<boolean>(false);
    const nonConcerneStatus = ref<IParameter>({} as IParameter);

    // Filtre persistant pour la liste des animaux
    const filterAnimaux = ref<IFilterAnimal>({
        page: 1,
        pageSize: 75000,
        state: undefined,
        search: "",
        isUsual: undefined,
        isReform: undefined,
        isPresent: undefined,
        IsRecentReform: undefined,
        idCenter: undefined,
        typeAnimal: "PO",
        workNumber: undefined,
        tattooIdentification: undefined,
        buckleNumber: undefined,
        karyotypeStatus: 0,
        lowDateKaryotype: "0",
        highDateKaryotype: "0",
        psLotId: undefined,
    } as IFilterAnimal);

    // Getters
    /**
     * Récupère la liste des animaux non réformés et qui sont présents dans le centre en cours de l'utilisateur (+ eventuellement les centres de quarantaine)
     */
    const getAnimauxInProductionCenter = computed(() => (centerId: number) => {
        return datas.value.filter((animal) => {
            return (animal.stud?.id == centerId || animal.stud?.parent?.id == centerId) &&
                animal.reform?.state != StateOfReform.Validate;
        });
    });

    /**
     * Verrat « commercialisable », au sens de REQ907 : hors quarantaine et apte à la conservation.
     * Règle extraite telle quelle de `Production.vue` (isBoarMarketable), seul endroit qui portait
     * les exclusions de quarantaine. Le bandeau PARC des compteurs ne s'en sert pas : il compte les
     * verrats placés dans le bâtiment (cf. `cubicleStore.getNbPlacedByGeneticType`, Mantis 0028608).
     * Ce prédicat ne concerne que l'écran Production. À NE PAS confondre avec le drapeau `IsCommercial`
     * des lignes Baseline
     * (critère QA3 du plan de monte, aptitude ∈ {1,2,3} ET statut 'P'), qui alimente
     * « Nb Verrat PDM Commer » et ignore la quarantaine.
     */
    const isMarketable = (animal: IBoar): boolean => {
        // Le centre de l'animal ne doit pas être un centre de quarantaine (un centre de
        // quarantaine est rattaché à un parent).
        if (animal.stud?.parent) return false;

        // L'animal ne doit pas être en statut Quarantaine.
        if (animal.productionStatus?.code3 == 'Q') return false;

        // Son aptitude de conservation ne doit être ni 3 ni 4.
        if (!animal.conservationAptitude) return false;
        const boarConsApt = +animal.conservationAptitude.code3!;
        if (boarConsApt == 3 || boarConsApt == 4 || Number.isNaN(boarConsApt)) return false;

        return true;
    };

    const getAnimauxFilter = computed(() => {
        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

        return datas.value.filter((animal) => {
            const reform = animal.reform;
            const karyotype = animal.karyotype;
            const isValidatedReform = reform?.state === StateOfReform.Validate;
            const reformDate = reform?.dateReform ? new Date(reform.dateReform) : null;
            const isRecentReform = isValidatedReform && reformDate && reformDate > oneYearAgo;

            // Filtre par état de réforme spécifique (garde aussi les animaux sans réforme)
            if (filterAnimaux.value.state != undefined && reform && reform.state != filterAnimaux.value.state) {
                return false;
            }

            const hasReformFilter = filterAnimaux.value.isPresent !== undefined
                || filterAnimaux.value.isReform !== undefined
                || filterAnimaux.value.isRecentReform !== undefined
                || filterAnimaux.value.isUsual !== undefined;

            if (hasReformFilter) {
                // isPresent: uniquement les animaux non réformés validés
                if (filterAnimaux.value.isPresent === true && isValidatedReform) {
                    return false;
                }

                // isReform: uniquement les animaux réformés validés
                if (filterAnimaux.value.isReform === true && !isValidatedReform) {
                    return false;
                }

                // isRecentReform: uniquement les animaux réformés validés depuis moins d'un an
                if (filterAnimaux.value.isRecentReform === true && !isRecentReform) {
                    return false;
                }

                // isUsual: animaux habituels (non réformés OU réformés depuis moins d'un an)
                if (filterAnimaux.value.isUsual === true) {
                    const isUsual = !isValidatedReform || isRecentReform;
                    if (!isUsual) {
                        return false;
                    }
                }
            } else {
                // Par défaut (comme sur develop) : exclure les animaux réformés validés
                if (isValidatedReform) {
                    return false;
                }
            }

            if (filterAnimaux.value.idCenter != undefined &&
                animal.stud?.id != filterAnimaux.value.idCenter &&
                animal.stud?.parent?.id != filterAnimaux.value.idCenter) {
                return false;
            }

            if (filterAnimaux.value.workNumber != undefined) {
                const workNumberLower = filterAnimaux.value.workNumber.toLowerCase();
                const matchesWorkNumber = animal.workNumber?.toLowerCase().includes(workNumberLower) ?? false;
                const matchesName = animal.name?.toLowerCase().includes(workNumberLower) ?? false;
                if (!matchesWorkNumber && !matchesName) {
                    return false;
                }
            }

            if (filterAnimaux.value.tattooIdentification != undefined) {
                const tattooLower = filterAnimaux.value.tattooIdentification.toLowerCase();
                const matchesTattoo = animal.tattooIdentification?.toLowerCase().includes(tattooLower) ?? false;
                if (!matchesTattoo) {
                    return false;
                }
            }

            if (filterAnimaux.value.buckleNumber != undefined) {
                const buckleLower = filterAnimaux.value.buckleNumber.toLowerCase();
                const matchesBuckle = animal.buckleNumber?.toLowerCase().includes(buckleLower) ?? false;
                if (!matchesBuckle) {
                    return false;
                }
            }

            if (filterAnimaux.value.karyotypeStatus != 0 &&
                karyotype?.status?.id != filterAnimaux.value.karyotypeStatus) {
                return false;
            }

            if (filterAnimaux.value.lowDateKaryotype && filterAnimaux.value.highDateKaryotype &&
                filterAnimaux.value.lowDateKaryotype != "0" && filterAnimaux.value.highDateKaryotype != "0") {
                let lowDate = new Date(filterAnimaux.value.lowDateKaryotype);
                let highDate = new Date(filterAnimaux.value.highDateKaryotype);
                lowDate.setHours(0, 0, 0, 0);
                highDate.setHours(23, 59, 59, 999);
                if (karyotype?.statusDate) {
                    let statusDate = new Date(karyotype.statusDate);
                    let timespanCondition = statusDate >= lowDate && statusDate <= highDate;
                    if (!timespanCondition) {
                        return false;
                    }
                }
            }
            return true;
        });
    });

    /**
     * Récupère les karyotypes filtrés selon les critères de filterAnimaux
     * Les karyotypes sont récupérés depuis les données inlinées sur chaque boar
     */
    const getFullKaryotype = computed((): (Partial<IKaryotype> & { animal: IBoar })[] => {
        return datas.value
            .filter((animal) => {
                // Filtre isPresent : exclure les animaux avec une réforme validée
                const isValidatedReform = animal.reform?.state === StateOfReform.Validate;
                if (filterAnimaux.value.isPresent === true && isValidatedReform) {
                    return false;
                }

                // Filtre par centre
                if (filterAnimaux.value.idCenter != undefined &&
                    animal.stud?.id != filterAnimaux.value.idCenter &&
                    animal.stud?.parent?.id != filterAnimaux.value.idCenter) {
                    return false;
                }

                // Filtre par numéro de travail ou nom
                if (filterAnimaux.value.workNumber != undefined) {
                    const workNumberLower = filterAnimaux.value.workNumber.toLowerCase();
                    const matchesWorkNumber = animal.workNumber?.toLowerCase().includes(workNumberLower) ?? false;
                    const matchesName = animal.name?.toLowerCase().includes(workNumberLower) ?? false;
                    if (!matchesWorkNumber && !matchesName) {
                        return false;
                    }
                }

                // Filtre par tatouage
                if (filterAnimaux.value.tattooIdentification != undefined) {
                    const tattooLower = filterAnimaux.value.tattooIdentification.toLowerCase();
                    const matchesTattoo = animal.tattooIdentification?.toLowerCase().includes(tattooLower) ?? false;
                    if (!matchesTattoo) {
                        return false;
                    }
                }

                // Filtre par numéro de boucle
                if (filterAnimaux.value.buckleNumber != undefined) {
                    const buckleLower = filterAnimaux.value.buckleNumber.toLowerCase();
                    const matchesBuckle = animal.buckleNumber?.toLowerCase().includes(buckleLower) ?? false;
                    if (!matchesBuckle) {
                        return false;
                    }
                }

                // Filtre par statut karyotype : uniquement pour les animaux ayant déjà un karyotype
                if (filterAnimaux.value.karyotypeStatus != 0 && animal.karyotype) {
                    if (animal.karyotype.status?.id != filterAnimaux.value.karyotypeStatus) {
                        return false;
                    }
                }

                // Filtre par plage de dates : uniquement pour les animaux ayant déjà un karyotype
                if (animal.karyotype &&
                    filterAnimaux.value.lowDateKaryotype && filterAnimaux.value.highDateKaryotype &&
                    filterAnimaux.value.lowDateKaryotype != "0" && filterAnimaux.value.highDateKaryotype != "0") {
                    let lowDate = new Date(filterAnimaux.value.lowDateKaryotype);
                    let highDate = new Date(filterAnimaux.value.highDateKaryotype);
                    lowDate.setHours(0, 0, 0, 0);
                    highDate.setHours(23, 59, 59, 999);
                    if (animal.karyotype.statusDate) {
                        let statusDate = new Date(animal.karyotype.statusDate);
                        let timespanCondition = statusDate >= lowDate && statusDate <= highDate;
                        if (!timespanCondition) {
                            return false;
                        }
                    }
                }

                return true;
            })
            .map(animal => ({ ...(animal.karyotype ?? {}), animal: animal as any }));
    });

    const isAnimalEditable = computed(() => (isAdmin: boolean) => {
        if (isAdmin) {
            return true;
        } else {
            var diffDateAdding = getDateDiff(new Date(itemForm.value.addingDate), new Date());

            if (diffDateAdding.day >= 0 && diffDateAdding.day < 20) {
                return true;
            } else {
                return false;
            }
        }
    });

    const getReformPreparationSortedAnimals = computed(() => {
        const withReform: DeepReadonly<IBoar>[] = [];
        const withoutReform: DeepReadonly<IBoar>[] = [];

        // Une seule itération pour partitionner (uniquement StandBy ou sans réforme)
        for (const animal of datas.value) {
            if (animal.reform) {
                // Inclure uniquement les réformes en StandBy
                if (animal.reform.state === StateOfReform.StandBy) {
                    withReform.push(animal);
                }
            } else {
                // Inclure les animaux sans réforme
                withoutReform.push(animal);
            }
        }

        // Tri par date de réforme
        withReform.sort((a, b) => {
            const dateA = a.reform?.dateReform ?? 0;
            const dateB = b.reform?.dateReform ?? 0;
            return dateA > dateB ? 1 : dateA < dateB ? -1 : 0;
        });

        // Tri par nom
        withoutReform.sort((a, b) => a.name.localeCompare(b.name));

        return [...withReform, ...withoutReform];
    });

    // Actions
    /**
     * Chargement des données dans le store
     * @param filter Le filtre à appliquer pour le chargement des données
     * @returns
     */
    async function load(filter: IBoarFilter, perfPage?: string) {
        const { start, end } = usePerformanceTracker(perfPage);
        isLoading.value = true;
        const loadSequence = nextBoarWriteSequence();

        try {
            start('GET /Boar (réseau)');
            let result = await BoarService.getAll<IBoarFilter, IBoarApiResponse>(filter);
            end('GET /Boar (réseau)', 'api');

            if (!result.error) {
                start('hydrateBoars()');
                datas.value = await hydrateBoars(result.data as IBoarApiResponse[]);
                end('hydrateBoars()', 'mapping');
            } else {
                datas.value = [];
            }
            lastLoadSequence = Math.max(lastLoadSequence, loadSequence);

            loadedCenterId.value = filter.idCenter;
            reformedLoaded.value = false;
            return result;
        } finally {
            isLoading.value = false;
        }
    }

    /**
     * Charge les animaux réformés et les fusionne dans datas.
     * Appelé à la demande (navigation vers les pages de réformes).
     */
    async function loadReformed(perfPage?: string) {
        if (reformedLoaded.value) return;

        // `reformedLoaded` n'est posé qu'à la fin : pendant les ~2 s de la requête, il vaut encore
        // false et tout appelant concurrent relance le même chargement de 3,8 Mo. Le Suivi de
        // fabrication en déclenchait 23 en parallèle (84 Mo, 26 s d'attente) via les Promise.all
        // imbriqués de hydrateLots → hydrateProductions. Le verrou d'appel en vol les fait
        // converger sur une seule requête ; la clé porte le centre, un chargement du centre A ne
        // devant jamais servir de réponse au centre B.
        return inFlight(`boars:reformed:${loadedCenterId.value}`, async () => {
            const { start, end } = usePerformanceTracker(perfPage);

            start('GET /Boar réformés (réseau)');
            const result = await BoarService.getAll<IBoarFilter, IBoarApiResponse>({
                page: 1,
                pageSize: 0,
                idCenter: loadedCenterId.value,
                isPresent: false,
            } as IBoarFilter);
            end('GET /Boar réformés (réseau)', 'api');

            if (!result.error) {
                start('hydrateBoars() réformés');
                const reformed = await hydrateBoars(result.data as IBoarApiResponse[]);
                end('hydrateBoars() réformés', 'mapping');

                // Fusionner : ajouter les réformés qui ne sont pas déjà présents
                const existingIds = new Set(datas.value.map(b => b.id));
                const newBoars = reformed.filter(b => !existingIds.has(b.id));
                datas.value = [...datas.value, ...newBoars];
            }

            reformedLoaded.value = true;
        });
    }

    /**
     * Hydrate une liste de boars en batch (optimisé pour la performance)
     * - Charge tous les stores dépendants une seule fois en parallèle
     * - Crée des Maps d'index pour des lookups O(1)
     * - Évite les appels répétés à useXxxStore()
     */
    async function hydrateBoars(boars: IBoarApiResponse[]): Promise<DeepReadonly<IBoar>[]> {
        if (boars.length === 0) return [];

        // 1. Récupérer les stores une seule fois
        const parameterStore = useParameterStore();
        const centerStore = useCenterStore();
        const thirdPartiesStore = useThirdPartiesStore();
        const ctgStore = useCTG();
        const breedingStudsStore = useBreedingStud();
        const psLotStore = usePSLotStore();
        // 2. Charger tous les stores dépendants en parallèle (une seule fois)
        const loadPromises: Promise<unknown>[] = [];

        // Chaque condition souffre du même défaut que le drapeau de loadReformed : elle est
        // évaluée par tous les appelants concurrents avant que le premier chargement n'ait pu
        // remplir le store. hydrateBoars étant lui-même appelé en parallèle (23 fois lors d'un
        // affichage du Suivi de fabrication), ces six chargements partaient autant de fois.
        if (parameterStore.datas.length === 0) {
            loadPromises.push(inFlight('parameter:all', () => parameterStore.load({ page: 1, pageSize: 0 })));
        }
        if (centerStore.datas.length === 0) {
            loadPromises.push(inFlight('center:all', () => centerStore.load({ page: 1, pageSize: 0 })));
        }
        if (!thirdPartiesStore.loadedAll) {
            loadPromises.push(inFlight('thirdParties:all', () => thirdPartiesStore.load({ page: 1, pageSize: 0 })));
        }
        if (ctgStore.listCTG.length === 0) {
            loadPromises.push(inFlight('ctg:all', () => ctgStore.loadListCTG()));
        }
        if (breedingStudsStore.datas.length === 0) {
            loadPromises.push(inFlight('breedingStud:all', () => breedingStudsStore.loadBreedingStud(1, 0)));
        }
        if (psLotStore.datas.length === 0) {
            loadPromises.push(inFlight('psLot:all', () => psLotStore.load({ page: 1, pageSize: 0 })));
        }

        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }

        // 3. Créer des Maps d'index pour des lookups O(1) au lieu de O(n)
        const parameterMap = new Map(parameterStore.datas.map(p => [p.id, p]));
        const centerMap = new Map(centerStore.datas.map(c => [c.id, c]));
        const thirdPartiesMap = new Map(thirdPartiesStore.datas.map(t => [t.id, t]));
        const ctgMap = new Map(ctgStore.listCTG.map(c => [c.id, c]));
        const breedingStudMap = new Map(breedingStudsStore.datas.map(b => [b.id, b]));
        const psLotMap = new Map(psLotStore.datas.map(p => [p.id, p]));

        // 4. Hydrater tous les boars en une seule passe
        const models: DeepReadonly<IBoar>[] = [];

        for (const boar of boars) {
            const model = { ...boar } as unknown as IBoar;

            // Paramètres obligatoires (lookup O(1) via Map)
            model.frozenAptitude = parameterMap.get(boar.frozenAptitudeId)!;
            model.conservationAptitude = parameterMap.get(boar.conservationAptitudeId)!;
            model.semenQuality = parameterMap.get(boar.semenQualityId)!;
            model.species = parameterMap.get(boar.speciesId)!;
            model.geneticType = parameterMap.get(boar.geneticTypeId)!;
            model.breed = parameterMap.get(boar.breedId)!;
            model.nn = parameterMap.get(boar.nnId)!;

            // Center (lookup O(1) via Map)
            model.stud = centerMap.get(boar.studId)!;

            // Third parties (lookup O(1) via Map)
            model.owner = thirdPartiesMap.get(boar.ownerId)!;
            model.provider = thirdPartiesMap.get(boar.providerId)!;

            // Paramètres optionnels (lookup O(1) via Map)
            if (boar.psLotId) {
                model.psLot = psLotMap.get(boar.psLotId);
            }
            if (boar.productionStatusId) {
                model.productionStatus = parameterMap.get(boar.productionStatusId);
            }
            if (boar.behaviourId) {
                model.behaviour = parameterMap.get(boar.behaviourId);
            }
            if (boar.exitWayId) {
                model.exitWay = parameterMap.get(boar.exitWayId);
            }
            if (boar.exitReasonId) {
                model.exitReason = parameterMap.get(boar.exitReasonId);
            }
            if (boar.collectDurationId) {
                model.collectDuration = parameterMap.get(boar.collectDurationId);
            }
            if (boar.ctgId) {
                model.ctg = ctgMap.get(boar.ctgId);
            }
            if (boar.breedingStudId) {
                model.breedingStud = breedingStudMap.get(boar.breedingStudId);
            }

            // Reform (depuis les données inlinées de l'API)
            if (boar.reformId) {
                model.reform = {
                    id: boar.reformId,
                    animalId: boar.id,
                    typeReform: parameterMap.get(boar.reformTypeReformId!)!,
                    reason: parameterMap.get(boar.reformReasonId!)!,
                    state: boar.reformState as StateOfReform,
                    dateReform: new Date(boar.reformDateReform!),
                    observations: boar.reformObservations,
                    lastGenerationDate: boar.reformLastGenerationDate
                        ? new Date(boar.reformLastGenerationDate) : undefined,
                } as IReform;
            }

            // Karyotype (depuis les données inlinées de l'API)
            if (boar.karyotypeId) {
                model.karyotype = {
                    id: boar.karyotypeId,
                    animalId: boar.id,
                    statusDate: new Date(boar.karyotypeStatusDate!),
                    status: parameterMap.get(boar.karyotypeStatusId!)!,
                    isActive: boar.karyotypeIsActive ?? true,
                } as IKaryotype;
            }

            models.push(model as DeepReadonly<IBoar>);
        }

        return models;
    }

    /**
     * Récupération d'un agent par son identifiant depuis l'api
     * @param id
     * @returns
     */
    async function get(id: number) {
        let result = await BoarService.getById<IBoarApiResponse>(id);
        // mettre à jour dans le store ?
        if (!result.error) {
            // Convertir le verrat en modèle complet avec les références des stores
            const [modelData] = await hydrateBoars([result.data as IBoarApiResponse]);

            let index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0)
                datas.value[index] = modelData;

            // on le stock dans le current puis dans le form
            current.value = modelData;
            itemForm.value = toForm(modelData);
        }
        return result;
    }

    /**
     * Relit depuis l'API des verrats déjà présents dans la liste et remplace leur ligne, sans
     * toucher au verrat courant ni au formulaire d'édition (à la différence de `get`).
     *
     * Pour une action qui modifie l'animal côté serveur hors de ce store : la mise en arrêt
     * depuis l'examen clinique (R12) notifie « MajAnimal », que ce store n'écoute pas — sans
     * relecture, la grille et les bandeaux d'identification gardent l'ancien état.
     *
     * Ne rejette jamais : rend les ids dont la relecture a échoué (ligne laissée en l'état).
     * Une réponse dépassée par une écriture plus récente de la ligne (autre relecture, MajBoar,
     * chargement complet) est ignorée sans être comptée en échec.
     */
    async function refreshBoars(ids: number[]): Promise<number[]> {
        const targets = [...new Set(ids)].filter((id) => datas.value.some((a) => a.id == id));
        const outcomes = await Promise.allSettled(targets.map(async (id) => {
            const sequence = nextBoarWriteSequence();
            const result = await BoarService.getById<IBoarApiResponse>(id);
            if (result.error || !result.data) throw new Error(`Relecture du verrat ${id} impossible`);
            const [modelData] = await hydrateBoars([result.data as IBoarApiResponse]);
            if (sequence < lastLoadSequence || sequence < (lastBoarWrite.get(id) ?? 0)) return;
            lastBoarWrite.set(id, sequence);
            const index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0) datas.value.splice(index, 1, modelData);
        }));
        return targets.filter((_, i) => outcomes[i].status === "rejected");
    }

    /**
     * Récupération de l'objet dans le store
     * @param id Identifiant de l'objet
     * @returns
     */
    async function searchStoreById(id: number) {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = await toForm(current.value as IBoar);
        return current.value;
    }

    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result = {} as ServiceOperationResult<IBoarApiResponse | IDataOperationResult<IBoarApiResponse>>;
        if (itemForm.value.id > 0) {
            if (isPatch) {
                result = await BoarService.patch(itemForm.value);
            } else {
                result = await BoarService.update(itemForm.value);
            }

            if (!result.error) {
                // Convertir le verrat en modèle complet avec les références des stores
                const [modelData] = await hydrateBoars([result.data as IBoarApiResponse]);
                current.value = modelData;
                if (maj) {
                    let index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                        return new ServiceOperationResult<IDataOperationResult<IBoarApiResponse>>(true, { errors: error });
                    }
                }
            }
        } else {
            result = await BoarService.create(itemForm.value);
            if (!result.error) {
                // Convertir le verrat créé en modèle complet avec les références des stores
                const [modelData] = await hydrateBoars([result.data as IBoarApiResponse]);
                current.value = modelData;
            }
        }
        return result;
    }

    /**
     * Récupération de la dernière quarantaine du centre
     * @param idCenter Quarantaine à vérifier
     * @returns Dernière quarantaine créée
     */
    async function getLastQuarantineCycle(idCenter: number) {
        const cycleQuarantaine = await BoarService.getLastQuarantineCycle(idCenter);
        if (!cycleQuarantaine.error) {
            return cycleQuarantaine.data;
        } else
            return "";
    }

    function toForm(item: IBoar): IBoarForm {
        const form = {} as IBoarForm;
        Object.assign(form, item);
        form.pslotId = item.psLot?.id;
        form.breedId = item.breed?.id;
        form.conservationAptitudeId = item.conservationAptitude?.id!;
        form.ownerId = item.owner?.id;
        form.providerId = item.provider?.id;
        form.studId = item.stud?.id;
        form.productionStateId = item.productionStatus?.id;
        // Repli sur l'id brut si l'élevage est absent du store, pour ne pas le perdre à l'enregistrement
        form.breedingStudId = item.breedingStud?.id ?? (item as IBoar & { breedingStudId?: number }).breedingStudId;
        form.behaviourId = item.behaviour?.id;
        form.exitReasonId = item.exitReason?.id;
        form.exitWayId = item.exitWay?.id;
        form.frozenAptitudeId = item.frozenAptitude?.id;
        form.geneticTypeId = item.geneticType?.id;
        form.ctgId = item.ctg?.id;
        form.collectDurationId = item.collectDuration?.id;
        form.nnId = item.nn?.id;
        form.semenQualityId = item.semenQuality?.id;
        return form;
    }

    /**
     * Permet d'initialiser un objet vide pour le formulaire
     */
    function newForm(lienId: number): IBoarForm {
        itemForm.value = {
            id: 0,
            studId: lienId,
        } as IBoarForm;
        return itemForm.value;
    }

    /**
     * Permet d'initialiser un objet vide pour les referentiels
     */
    function newItem(lien: ICenter): DeepReadonly<IBoar> {
        current.value = {
            id: 0,
            stud: lien,
        } as DeepReadonly<IBoar>;
        return current.value;
    }

    /**
     * Retourne un animal par son identifiant depuis le store
     */
    function getByIdInStore(id: number, centerId?: number): DeepReadonly<IBoar> | undefined {
        return datas.value.find((animal) => animal.id === id && (!centerId || animal.stud?.id === centerId));
    }

    // Retourner les propriétés et méthodes publiques
    return {
        // State
        datas,
        current,
        itemForm,
        isLoading,
        loadedCenterId,
        nonConcerneStatus,
        filterAnimaux,

        // Getters
        getAnimauxInProductionCenter,
        isMarketable,
        getAnimauxFilter,
        getFullKaryotype,
        isAnimalEditable,
        getReformPreparationSortedAnimals,

        // Actions
        load,
        loadReformed,
        get,
        refreshBoars,
        searchStoreById,
        save,
        getLastQuarantineCycle,
        hydrateBoars,
        toForm,
        newForm,
        newItem,
        getByIdInStore,
    };
});

signalRManager.connection.on("MajBoar", async (payload: IPayloadStore) => {
    const store = useBoars();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateBoars([payload.data as IBoarApiResponse]);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            // État poussé à l'arrivée : une relecture partie avant ne doit plus l'écraser.
            if (payload.data?.id != null) lastBoarWrite.set(payload.data.id, nextBoarWriteSequence());
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateBoars([payload.data as IBoarApiResponse]);
                store.datas[idx] = hydrated;
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});

export { useBoars };
